import { useMemo, useEffect, useCallback, useState, useRef } from 'react';
import styled, { css } from 'styled-components';

import type { PrismicCustomData } from '@yoweb/prismic-slicemachine/types/prismic-types';
import type { Primary, Item } from '@yoweb/prismic-slicemachine/slices/FaqSlice/FaqSliceTypes';
import { mapQuestionItems } from '@yoweb/prismic-slicemachine/components/faq/FaqGroupQuestions';
import { type ReversedAppDomains } from '@yoweb/prismic-slicemachine/constants/config';
import { media, pxToRem, gridBase, breakpoints } from '@yoweb/ui/styles/utils';
import { th } from '@yoweb/ui/styles/utils/theme';
import { Title } from '@yoweb/ui/components/typography';
import { Accordion, AccordionItemStyleMode } from '@yoweb/ui/components/marketing/Accordion';
import { Container } from '@yoweb/ui/components/containers';
import { Box } from '@yoweb/ui/components/Box';
import { TabVertical, TabVerticalContainer } from '@yoweb/ui/components/Tabs';
import type { RefsObject } from '@yoweb/ui/components/marketing/Accordion/Accordion';
import {
  findActiveTab,
  type FaqGroupData,
} from '@yoweb/prismic-slicemachine/components/faq/FaqGroupTab';

const IDS = {
  faqMenu: 'faq-section-menu',
  faqSectionDesktop: 'faq-section-desktop',
  faqSectionDesktopQH: 'faq-section-desktop-qh',
  faqSectionDesktopTitle: 'faq-section-desktop-title',
  faqSectionMobile: 'faq-section-mobile',
  faqSectionMobileTitle: 'faq-section-mobile-title',
  faqSectionMobileQH: 'faq-section-mobile-qh',
  submit: 'faq-question-submit',
  form: 'faq-question-form',
  error: 'faq-question-error',
  success: 'faq-question-success',
};

const SCROLL_DELAY_MS = 400;

type FaqGroupViewProps = {
  query: {
    section: string;
    question: string;
  };
  prismicData: PrismicCustomData<Primary, Item> | null;
  reversedDomains: ReversedAppDomains;
  internalURLs: string[];
} & DataTestId;

export const FaqGroupView = ({
  query,
  prismicData,
  reversedDomains,
  internalURLs,
}: FaqGroupViewProps) => {
  // --- FAQ related
  const mappedData: FaqGroupData[] | null = useMemo(
    () =>
      mapQuestionItems({
        prismicData,
        reversedDomains,
        internalURLs,
      }),
    [prismicData?.data.slices],
  );

  const getActiveTab = useCallback(findActiveTab, [mappedData]);

  const [activeTab, setActiveTab] = useState(() => getActiveTab(mappedData || [], query));

  useEffect(
    () => setActiveTab(getActiveTab(mappedData || [], query)),
    [mappedData, query, getActiveTab],
  );

  const refs = useRef<RefsObject>({});
  // Scroll to the category during initial load.
  useEffect(() => {
    const isMobile = window.matchMedia(`(max-width: ${breakpoints.md - 1}px)`).matches;
    const tabId = `${isMobile ? '' : 'desktop-'}${activeTab}`;
    const categoryHeader = document.querySelector(`[data-idx="${tabId}"]`);

    let timeOut: NodeJS.Timeout | undefined;
    if (categoryHeader && (query.question || query.section) && activeTab) {
      // Adding a short delay before scrolling to wait for the banner to
      // be rendered. A better fix will be to server render the banner.
      timeOut = setTimeout(() => {
        if (refs.current?.[query.question]) {
          const element = refs.current[query.question];
          if (element !== null) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, SCROLL_DELAY_MS);
    } else {
      window.scrollTo({ top: 0 });
    }
    return () => {
      if (timeOut) {
        clearTimeout(timeOut);
        timeOut = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = mappedData ? mappedData.map((d) => d?.category) : [];

  return (
    <>
      <FAQLayoutMobile key="faq-mobile" data-testid={IDS.faqSectionMobile}>
        {mappedData?.map((faqSection, idx) => {
          if (!faqSection) {
            return null;
          }
          const isOpenIdx = faqSection?.questions.findIndex((q) => q.questionId === query.question);
          return (
            <QuestionHolderMobile
              key={`${faqSection.category}-mobile`}
              data-testid={IDS.faqSectionMobileQH}
            >
              <Title data-testid={IDS.faqSectionMobileTitle} size="sm" data-idx={idx}>
                {faqSection.category}
              </Title>
              <Accordion
                itemStyleMode={AccordionItemStyleMode.JUSTIFIED}
                data={faqSection.questions}
                id={`${idx}-faq`}
                isOpenIndex={isOpenIdx}
                refs={refs}
              />
            </QuestionHolderMobile>
          );
        })}
      </FAQLayoutMobile>
      <FAQLayoutDesktop mt={88} mb={72} key="faq-desktop" data-testid={IDS.faqSectionDesktop}>
        <ContainerGrid>
          <FAQMenu data-testid={IDS.faqMenu}>
            <TabVerticalContainer>
              {categories.map((category, idx) => (
                <TabVertical
                  key={category}
                  isActive={idx === activeTab}
                  onClick={() => setActiveTab(idx)}
                >
                  {category}
                </TabVertical>
              ))}
            </TabVerticalContainer>
          </FAQMenu>
          {mappedData?.map((data, idx) => {
            const isOpenIdx = data?.questions.findIndex((q) => q.questionId === query.question);
            return (
              <QuestionHolder
                data-testid={IDS.faqSectionDesktopQH}
                $isVisible={idx === activeTab}
                key={`faq-section-${idx}`}
              >
                <Title
                  data-testid={IDS.faqSectionDesktopTitle}
                  size={{ _: 'sm', md: 'md' }}
                  data-idx={`desktop-${idx}`}
                >
                  {data?.category}
                </Title>
                <Accordion
                  data={data?.questions ?? []}
                  id={`faq-tab-${idx}`}
                  isOpenIndex={isOpenIdx}
                  refs={refs}
                />
              </QuestionHolder>
            );
          })}
        </ContainerGrid>
      </FAQLayoutDesktop>
    </>
  );
};

const FAQLayoutMobile = styled.div`
  && {
    background-color: ${th.getColor('base000')};
    display: block;
    margin-top: ${pxToRem(60)};
    padding-bottom: ${pxToRem(72)};
    ${media.md`
      display: none;
    `}
  }
`;
const FAQLayoutDesktop = styled(Box)`
  && {
    display: none;
    ${media.md`
      display: block;
    `}
  }
`;
const FAQMenu = styled.div`
  grid-column: 1/3;
  ${media.lg`
    grid-column: 1/6;
  `}
`;

const ContainerGrid = styled(Container)`
  && {
    ${gridBase};
  }
`;

const QuestionHolder = styled.div<{ $isVisible?: boolean }>`
  display: grid;
  grid-gap: ${th.getSpace('normal2')};
  padding: ${pxToRem(40)} ${pxToRem(72)};
  grid-column: 3/9;
  background-color: ${th.getColor('base000')};
  border-radius: ${th.getRadii('medium')};

  ${({ $isVisible }) =>
    !$isVisible &&
    css`
      display: none;
    `}

  ${media.lg`
    padding: ${pxToRem(64)} ${pxToRem(104)};
    grid-column: 6/24;
  `}
`;

const QuestionHolderMobile = styled.div`
  padding: ${pxToRem(56)} ${pxToRem(24)} 0;
`;
